import React, { Component } from 'react';
import { withRouter } from 'react-router';
import {
  Button,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Row,
  UncontrolledTooltip
} from 'reactstrap';
import { get } from 'lodash-es';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPenSquare } from '@fortawesome/free-solid-svg-icons';
import { shippingApi } from '../../api/shipping';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import axios from 'axios';

import InlineAlert from '../../utilities/form-components/InlineAlert';
import { cfPath } from '../../utilities/UrlPaths';

const errorLoadingTermsText = 'Unable to load your auction terms. Please try again later or contact Customer Care any time at:';

const popoverSaleText = 'Please select Live or Timed for your sale. Live auctions allow online bidders to compete with the floor bidders at your auction house. Timed auctions allow you to hold online-only auctions. Contact us today at 617-746-9800 to learn more.';
const popoverTermsText = 'The following terms are eligible for payment processing. In order to enable a greyed out set of terms, please edit the terms to require credit cards for screening.';
const popovershippingTermsText = 'Select the Shipping Terms you created from your onboarding experience. Contact customercare@invaluable.com to edit your terms.';
/**
 * AddSale represents the stateful component form called from the upcoming auction calendar page.
 * This component is a little large and can ideally be broken down, but that's the nature of forms
 * in React.
 *
 * There is surprisingly a lot of business logic in this simple form, live/timed houses require that
 * terms be loaded.  These terms themselves can be enabled or disabled in
 * the dropdown.  Sometimes we show different options available to the
 * user based on their selections, etc etc.  Also validation.
 */
class AddSale extends Component {
  constructor(props) {
    super(props);
    this.state = {
      addSaleRadio: 'live',
      binStep: 1,
      errorAddingSale: false,
      errorLoadingTerms: false,
      saleName: '',
      selectedTerms: '',
      selectedShippingTerms: '',
      validation: {
        saleName: {
          touched: false
        },
        selectedTerms: {
          touched: false
        },
        selectedShippingTerms: {
          touched: false
        }
      }
    };
  }

  async componentDidMount() {
    /**
     * Rename these since there are very similarly yet
     * equally confusingly named attributes at the terms level <3
    */
    const {
      houseID,
      liveOrTimedEnabled,
      isHouseFeeStepActive: houseTieredEnabled,
      paymentProcessingEnabled: housePPEnabled
    } = this.props.house;

    if (!liveOrTimedEnabled) {
      return;
    }
    let binStep = 1;

    try {
      const accountID = houseID;
      const accountDomain = 'AUCTIONHOUSE';
      const response = await shippingApi.getShippingPolicy(accountID, accountDomain);
      if(response.data){
        const shippingPolicys = get(response, 'data.policy', []);
        this.setState({ binStep, shippingTerms: shippingPolicys, isShippingEnabled: true });
      } else if(response.error.code === 'POLICY_NOT_FOUND'){
        this.setState({ binStep, isShippingEnabled: false });
      }
    } catch {
      this.setState({ errorLoadingTerms: true });
    }

    try {
      const termsUrl = `/cps/app/house/${houseID}/terms`;

      // Upon clicking 'Add Sale', we need to fetch the terms for the dropdown
      const response = await axios(termsUrl);

      const groupSettings = get(response, 'data.data.groupSettings', []);
      const terms = groupSettings.map(term => {
        // Rename similarly named variables so that they are like... 1% easier to comprehend
        const {
          houseFeeStepEligible: termTieredEnabled,
          paymentProcessingEligible: termPPEnabled
        } = term;

        /**
         * (dyouberg) Ok so we need to disable terms when the house payment processing does not
         * match the term payment processing. OR if the house tiered pricing does not match
         * the term tiered pricing.  Simple and not confusing at all.
         */
        const termTieredEligible = houseTieredEnabled === termTieredEnabled;
        if (housePPEnabled) {
          const termPPEligible = housePPEnabled === termPPEnabled;
          term.disabled = !(termPPEligible && termTieredEligible);
        } else {
          term.disabled = !termTieredEligible;
        }
        return term;
      });

      this.setState({ binStep, terms });
    } catch {
      this.setState({ errorLoadingTerms: true });
    }
  }

  /************************************************************************
   * START BUTTON HANDLERS / VALIDATION
   ***********************************************************************/
  handleAddSaleRadio = value => {
    this.setState({ addSaleRadio: value });
  };

  handleSaleName = event => {
    const { validation } = this.state;
    validation.saleName.touched = true;

    if (event.target.value.length > 0) {
      delete validation.saleName.error;
    }
    this.setState({ validation, saleName: event.target.value });
  };

  handleSelectTerms = event => {
    const { validation } = this.state;
    if(event.currentTarget.id === 'selectTerms'){
      if (event.target.value.length > 0) {
        delete validation.selectedTerms.error;
      }
      validation.selectedTerms.touched = true;
      this.setState({
        validation,
        selectedTerms: event.target.value
      });
    } else {
      if (event.target.value.length > 0) {
        delete validation.selectedShippingTerms.error;
      }
      validation.selectedShippingTerms.touched = true;
      this.setState({
        validation,
        selectedShippingTerms: event.target.value
      });
    }
  };

  validateFields = () => {
    const { saleName, selectedTerms, validation } = this.state;

    if (!saleName) {
      validation.saleName.error = 'Sale title is required';
    } else {
      delete validation.saleName.error;
    }

    if (!selectedTerms) {
      validation.selectedTerms.error = 'Please select your terms';
    } else {
      delete validation.selectedTerms.error;
    }

    this.setState({ validation });
  };
  /************************************************************************
   * END BUTTON HANDLERS / VALIDATION
   ***********************************************************************/

  submitAddSale = () => {
    const { house: { houseID } } = this.props;
    const { addSaleRadio } = this.state;
    this.submitAddSaleLiveTimed(addSaleRadio, houseID);
  };

  submitAddSaleLiveTimed = async (addSaleRadio, houseID) => {
    try {
      const { fetchCalendar } = this.props;
      const { saleName, selectedTerms, selectedShippingTerms } = this.state;

      let isTimed = '0';
      if (addSaleRadio === 'timed') {
        isTimed = '1';
      }

      let postBody;
      if(!this.state.isShippingEnabled) {
        // Create the request body as BE wants it
        postBody = {
          isTimed,
          title: saleName,
          termsID: selectedTerms
        };
      } else {
        postBody = {
          isTimed,
          title: saleName,
          termsID: selectedTerms,
          defaultShippingTermsID: selectedShippingTerms,
        };
      }

      const postHeaders = {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        }
      };

      await axios.post(`/cps/app/house/${houseID}/auction`, JSON.stringify(postBody), postHeaders);
      fetchCalendar();
    } catch {
      this.setState({ errorAddingSale: true });
    }
  };

  render() {
    const { house, toggleAddSale } = this.props;
    const {
      addSaleRadio,
      errorAddingSale,
      errorLoadingTerms,
      isShippingEnabled,
      saleName,
      selectedTerms,
      selectedShippingTerms,
      terms,
      shippingTerms,
      validation
    } = this.state;

    const { liveOrTimedEnabled } = house;
    let disableButton;
    if (!isShippingEnabled) {
      disableButton = !(
        saleName.length > 0 &&
        selectedTerms.length > 0 &&
        addSaleRadio.length > 0
      );

    } else {
      disableButton = !(
        saleName.length > 0 &&
        selectedTerms.length > 0 &&
        selectedShippingTerms.length > 0 &&
        addSaleRadio.length > 0
      );
    }

    return (
      <div className="add-sale-row mb-3">
        <h3 className="mb-3">Create New Sale</h3>

        {errorLoadingTerms && <InlineAlert baseText={errorLoadingTermsText} />}
        {errorAddingSale && <InlineAlert />}

        <strong id="saleType">
          Sale Type:{' '}
          {liveOrTimedEnabled && (
            <FontAwesomeIcon className="color-blue" icon="question-circle" />
          )}
        </strong>
        {liveOrTimedEnabled && (
          <UncontrolledTooltip placement="right" target="saleType">
            {popoverSaleText}
          </UncontrolledTooltip>
        )}

        <Form className="mt-2">
          {liveOrTimedEnabled && (
            <div>
              <FormGroup check>
                <Label check>
                  <Input
                    type="radio"
                    name="saleTypeRadio"
                    checked={addSaleRadio === 'live'}
                    onChange={() => this.handleAddSaleRadio('live')}
                  />{' '}
                  <FontAwesomeIcon icon="rss" /> Live Auction
                </Label>
              </FormGroup>

              <FormGroup check className="mt-2">
                <Label check>
                  <Input
                    type="radio"
                    name="saleTypeRadio"
                    checked={addSaleRadio === 'timed'}
                    onChange={() => this.handleAddSaleRadio('timed')}
                  />{' '}
                  <FontAwesomeIcon icon="clock" /> Timed Auction
                </Label>
              </FormGroup>
            </div>
          )}

          {liveOrTimedEnabled && (
            <div>
              <FormGroup className="my-3">
                <Label for="addSaleName">
                  <strong>Name</strong> (max. 120 characters) This is for
                  internal use only - your bidders cannot see it.
                </Label>
                <Input
                  className={
                    validation.saleName.touched && validation.saleName.error
                      ? 'is-invalid'
                      : ''
                  }
                  type="text"
                  name="text"
                  id="addSaleName"
                  maxLength="120"
                  onBlur={() => this.validateFields()}
                  onChange={e => this.handleSaleName(e)}
                  value={saleName}
                />
                {validation.saleName.touched && validation.saleName.error && (
                  <p className="color-danger my-0">
                    {validation.saleName.error}
                  </p>
                )}
              </FormGroup>

              <FormGroup className="my-3">
                <Label for="selectTerms">
                  <strong id="termsText">
                    Auction Terms:{' '}
                    <FontAwesomeIcon
                      className="color-blue"
                      icon="question-circle"
                    />
                  </strong>
                  <UncontrolledTooltip placement="right" target="termsText">
                    {popoverTermsText}
                  </UncontrolledTooltip>
                </Label>
                <Input
                  className={
                    validation.selectedTerms.touched
                      && validation.selectedTerms.error
                      ? 'is-invalid'
                      : ''
                  }
                  type="select"
                  name="select"
                  id="selectTerms"
                  value={selectedTerms}
                  onBlur={() => this.validateFields()}
                  onChange={e => this.handleSelectTerms(e)}
                >
                  <option value="">Select Terms</option>
                  {terms
                    && terms.map(term => (
                      <option
                        key={term.id}
                        value={term.id}
                        disabled={term.disabled}
                      >
                        {term.name}
                      </option>
                    ))}
                </Input>
                {validation.selectedTerms.touched
                  && validation.selectedTerms.error && (
                    <p className="color-danger my-0">
                      {validation.selectedTerms.error}
                    </p>
                )}
              </FormGroup>

              <FontAwesomeIcon icon={faPenSquare} color="#004BD6"/>
              <a href={`${cfPath()}/al/settings/groupSettings.cfm`} className="pl-1">
                Edit Auction Terms
              </a>
              {isShippingEnabled && (
                <FormGroup className="my-3">
                  <Label for="selectShippingTerms" className="mb-3">
                    <Stack direction="row" spacing={1}>
                      <Chip label="NEW" color="primary" sx={{height: '22px'}}/>
                      <strong id="shippingTermsText">
                        Shipping Terms:{' '}
                        <FontAwesomeIcon
                          className="color-blue"
                          icon="question-circle"
                        />
                      </strong>
                      <UncontrolledTooltip placement="right" target="shippingTermsText">
                        {popovershippingTermsText}
                      </UncontrolledTooltip>
                    </Stack>
                  </Label>
                  <Input
                    className={
                      validation.selectedTerms.touched
                        && validation.selectedTerms.error
                        ? 'is-invalid'
                        : ''
                    }
                    type="select"
                    name="select"
                    id="selectShippingTerms"
                    value={selectedShippingTerms}
                    onBlur={() => this.validateFields()}
                    onChange={e => this.handleSelectTerms(e)}
                  >
                    <option value="">Select Policy</option>
                    {shippingTerms
                      && shippingTerms.terms.map(term => (
                        <option
                          key={term.id}
                          value={term.id}
                          disabled={term.flag}
                        >
                          {term.name}
                        </option>
                      ))}
                  </Input>
                  {validation.selectedTerms.touched
                    && validation.selectedTerms.error && (
                      <p className="color-danger my-0">
                        {validation.selectedTerms.error}
                      </p>
                  )}
                </FormGroup>
              )}
            </div>
          )}

          <Row className="mt-3">
            <Col xs={12}>
              {/* Note: ReactStrap uses the color attribute to determine the
              styles - with secondary being the default.  For instance this
              results in <button class="btn btn-primary"></button> */}
              <Button
                color="primary"
                className="mr-3 cursor-not-allowed"
                disabled={disableButton}
                onClick={() => this.submitAddSale()}
              >
                Continue
              </Button>
              <Button color="link" onClick={() => toggleAddSale(false)}>
                Cancel
              </Button>
            </Col>
          </Row>
        </Form>
      </div>
    );
  }
}

export default withRouter(AddSale);
